// Standard benefits: the ones every employee has, or comes to have, without
// spending flexible points. Each carries its criteria in words for people to
// read, and up to three rules the app can check against a person's record:
//
//   permanent_only      fixed-term staff do not qualify
//   min_service_months  qualifies this many whole months after the start of
//                       employment; 0 or blank is from the first day
//   requires_probation  qualifies once probation is marked Passed on the
//                       People record; a record with no probation on it at
//                       all counts as passed (see probationPosition)
//
// Anything the record cannot answer (a pension salary threshold, say) belongs
// in the eligibility text, and the person reads it there. Contractors are
// outside benefits altogether, as they are for flexible benefits.
//
// Shared by the API (who qualifies, for HR and for My Benefits) and the
// screens, so the date a person is told and the date HR sees are the same.
import { isEmployee } from './employment.js';

/** start + months, as YYYY-MM-DD, clamped to the month's last day (31 Aug + 6 months = 28 or 29 Feb). */
export function addMonths(isoDate, months) {
  const [y, m, d] = String(isoDate).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const total = (m - 1) + months;
  const ty = y + Math.floor(total / 12);
  const tm = ((total % 12) + 12) % 12;
  const last = new Date(Date.UTC(ty, tm + 1, 0)).getUTCDate();
  return `${ty}-${String(tm + 1).padStart(2, '0')}-${String(Math.min(d, last)).padStart(2, '0')}`;
}

/** The date a person's service counts from: the real start, or the date they joined Ops for a record with none. */
export const serviceStart = (person) => person?.start_of_employment || person?.date_of_joining || null;

/**
 * Where a person's probation stands, from people.probation_status and
 * probation_end:
 *   'passed'   marked Passed
 *   'none'     nothing recorded: Not Started (the column default) or blank,
 *              with no end date. Long-serving staff look like this because
 *              Ops did not record probation when they joined, so it counts
 *              as passed. A new starter's onboarding writes the end date, so
 *              they do not.
 *   'failed'   marked Failed
 *   'open'     anything else: In Progress, Extended, or an end date with the
 *              outcome not yet recorded
 */
export function probationPosition(person) {
  const status = String(person?.probation_status || '').trim().toLowerCase();
  if (status === 'passed') return 'passed';
  if (status === 'failed') return 'failed';
  if ((status === '' || status === 'not_started') && !person?.probation_end) return 'none';
  return 'open';
}

/**
 * Where a person stands on one standard benefit on `today` (YYYY-MM-DD):
 *   { status: 'eligible', note? }                 note says why an unrecorded probation counted
 *   { status: 'pending', from?, reason?, probation_end? }
 *                                                 from: qualifies on that date on service alone;
 *                                                 reason: waiting on probation being passed
 *   { status: 'not_eligible', reason }            the rules exclude them
 *   { status: 'unknown', reason }                 the record cannot say (no start date)
 */
export function standardBenefitStatus(benefit, person, today) {
  if (!isEmployee(person?.employment_type)) {
    return { status: 'not_eligible', reason: 'Benefits are for employees, not contractors.' };
  }
  if (benefit.permanent_only && String(person?.employment_type || '').trim().toLowerCase() === 'fixed_term') {
    return { status: 'not_eligible', reason: 'Permanent staff only.' };
  }
  const probation = benefit.requires_probation ? probationPosition(person) : null;
  if (probation === 'failed') return { status: 'not_eligible', reason: 'Probation not passed.' };

  let from = null;
  const months = Number(benefit.min_service_months) || 0;
  if (months > 0) {
    const start = serviceStart(person);
    if (!start) return { status: 'unknown', reason: 'Your start date is not recorded, so HR will confirm.' };
    const reached = addMonths(start, months);
    if (reached > today) from = reached;
  }
  if (probation === 'open') {
    const out = { status: 'pending', reason: 'Probation not yet marked passed.' };
    if (from) out.from = from;
    if (person?.probation_end) out.probation_end = String(person.probation_end).slice(0, 10);
    return out;
  }
  if (from) return { status: 'pending', from };
  if (probation === 'none') return { status: 'eligible', note: 'No probation on record, so counted as passed.' };
  return { status: 'eligible' };
}

/** The rules in words, for HR's table and the employee's card. */
export function standardBenefitRules(benefit) {
  const parts = [];
  const months = Number(benefit.min_service_months) || 0;
  // Whole years read as years; the possessive follows the number (1 month's, 6 months').
  const [n, unit] = months % 12 === 0 ? [months / 12, 'year'] : [months, 'month'];
  const service = months > 0 ? `${n} ${unit}${n === 1 ? "'s" : "s'"} service` : null;
  if (benefit.requires_probation) parts.push(service ? `After ${service} and passing probation` : 'After passing probation');
  else parts.push(service ? `After ${service}` : 'From your first day');
  if (benefit.permanent_only) parts.push('permanent staff only');
  return parts.join(', ');
}
